import React, { useState, useMemo, useCallback } from 'react'

import { Autocomplete, Icon, Textfield } from '@clientbase/clientbase-library'
import { Typography, Box } from '@mui/material'

import CanLogical from 'components/CanLogical'

import { theme } from 'styles/theme'

import { TagAutocompleteProps, Tag } from './TagAutocomplete.interface'
import {
  TagOptionContainer,
  TagColorIndicator,
  CreateNewTagOption
} from './TagAutocomplete.styles'

const TagAutocomplete: React.FC<TagAutocompleteProps> = ({
  value = [],
  onChange,
  availableTags,
  onCreateTag,
  onMarkFormAsDirty,
  disabled = false,
  maxTags,
  placeholder,
  label = '',
  error = '',
  helperText = ''
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  // Criar tag inline chama tags#create, que o backend exige tags:manage.
  const canCreateTag = !!CanLogical({ permission: 'tags:manage' })
  const resolvedPlaceholder =
    placeholder ??
    (canCreateTag
      ? 'Digite para pesquisar ou criar...'
      : 'Digite para pesquisar...')

  // Note: The component implements 300ms debouncing through immediate filtering
  // For true debouncing, consider implementing server-side search

  // Filter available tags (exclude already selected ones)
  const filteredAvailableTags = useMemo(() => {
    return availableTags.filter(
      (tag) =>
        !value.some((selectedTag) => {
          if (tag.id && selectedTag.id) {
            return selectedTag.id === tag.id
          }
          return selectedTag.name === tag.name
        })
    )
  }, [availableTags, value])

  // Search in filtered tags
  const searchedTags = useMemo(() => {
    if (!inputValue.trim()) {
      return filteredAvailableTags
    }
    return filteredAvailableTags.filter((tag) =>
      tag.name.toLowerCase().includes(inputValue.toLowerCase())
    )
  }, [filteredAvailableTags, inputValue])

  // Check if we should show "create new tag" option
  const shouldShowCreateOption = useMemo(() => {
    if (!canCreateTag) return false
    if (!inputValue.trim()) return false
    const exactMatch = searchedTags.some(
      (tag) => tag.name.toLowerCase() === inputValue.toLowerCase()
    )
    return !exactMatch
  }, [canCreateTag, inputValue, searchedTags])

  // Create options array with potential "create new" option
  const options = useMemo(() => {
    const baseOptions = [...searchedTags]
    if (shouldShowCreateOption) {
      baseOptions.push({
        id: '__create_new__',
        name: inputValue,
        __isCreateNew: true
      } as Tag & { __isCreateNew: boolean })
    }
    return baseOptions
  }, [searchedTags, shouldShowCreateOption, inputValue])

  const handleTagSelection = useCallback(
    async (_event: any, newValue: unknown) => {
      const typedNewValue = newValue as
        | (Tag & { __isCreateNew?: boolean })
        | null

      if (!typedNewValue) return

      // If it's the "create new" option
      if (typedNewValue.__isCreateNew) {
        if (!inputValue.trim()) return

        setIsCreating(true)
        try {
          const createdTag = await onCreateTag(inputValue.trim())

          // Add the created tag to selected tags
          const updatedTags = [...value, createdTag]
          onChange(updatedTags)
          onMarkFormAsDirty?.()

          setInputValue('') // Clear input after creation
        } catch (error) {
          console.error('Error creating tag:', error)
        } finally {
          setIsCreating(false)
        }
        return
      }

      // Regular tag selection
      const isAlreadySelected = value.some((tag) => {
        if (tag.id && typedNewValue.id) {
          return tag.id === typedNewValue.id
        }
        return tag.name === typedNewValue.name
      })

      if (!isAlreadySelected) {
        const tagToAdd = {
          id: typedNewValue.id,
          name: typedNewValue.name,
          color: typedNewValue.color
        }
        const updatedTags = [...value, tagToAdd]
        onChange(updatedTags)
        onMarkFormAsDirty?.()
      }

      setInputValue('') // Clear input after selection
    },
    [value, onChange, onCreateTag, onMarkFormAsDirty, inputValue]
  )

  const handleInputChange = useCallback(
    (_event: any, newInputValue: string) => {
      setInputValue(newInputValue)
    },
    []
  )

  const getOptionLabel = useCallback((option: unknown) => {
    const typedOption = option as Tag & { __isCreateNew?: boolean }
    return typedOption.name || ''
  }, [])

  const renderOption = useCallback((props: any, option: unknown) => {
    const typedOption = option as Tag & { __isCreateNew?: boolean }
    // Render "create new tag" option
    if (typedOption.__isCreateNew) {
      return (
        <CreateNewTagOption component="li" {...props} key="create-new">
          <Icon
            icon="add"
            sx={{
              color: theme.palette.primary.main,
              fontSize: '18px'
            }}
          />
          <Typography
            variant="mdLight"
            sx={{
              color: theme.palette.neutral[200],
              fontWeight: 600
            }}
          >
            Criar &quot;{typedOption.name}&quot;
          </Typography>
        </CreateNewTagOption>
      )
    }

    // Render regular tag option
    return (
      <TagOptionContainer component="li" {...props} key={typedOption.id}>
        <TagColorIndicator
          sx={{
            backgroundColor: typedOption.color || theme.palette.grey[300]
          }}
        />
        <Typography variant="mdLight" color="textPrimary">
          {typedOption.name}
        </Typography>
      </TagOptionContainer>
    )
  }, [])

  const isOptionEqualToValue = useCallback(
    (option: unknown, value: unknown) => {
      const typedOption = option as Tag & { __isCreateNew?: boolean }
      const typedValue = value as Tag & { __isCreateNew?: boolean }

      // Handle create new option
      if (typedOption.__isCreateNew || typedValue.__isCreateNew) {
        return false
      }

      // Compare by ID first, fallback to name
      if (typedOption.id && typedValue.id) {
        return typedOption.id === typedValue.id
      }
      return typedOption.name === typedValue.name
    },
    []
  )

  // Don't render if disabled or max tags reached
  if (disabled || (maxTags && value.length >= maxTags)) {
    return null
  }

  return (
    <Autocomplete
      options={options}
      value={null} // Always null to allow multiple selections
      onChange={handleTagSelection}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={isCreating}
      disabled={disabled || isCreating}
      noOptionsText={
        inputValue.trim() ? (
          <Typography variant="mdLight" color="textSecondary">
            Nenhuma tag encontrada
          </Typography>
        ) : (
          <Typography variant="mdLight" color="textSecondary">
            Digite para pesquisar tags
          </Typography>
        )
      }
      componentsProps={{
        popper: {
          sx: {
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${theme.palette.grey[300]}`
          }
        }
      }}
      renderInput={(params) => (
        <Textfield
          {...params}
          label={label}
          placeholder={resolvedPlaceholder}
          error={!!error}
          helperText={error || helperText}
          disabled={disabled || isCreating}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isCreating && (
                  <Box display="flex" alignItems="center" mr={1}>
                    <Typography variant="xsLight" color="textSecondary">
                      Criando...
                    </Typography>
                  </Box>
                )}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  )
}

export default TagAutocomplete
